// Claude API Service for Conversational AI

import { ConversationContext, ConversationResponse } from './conversationalAgent';

export interface ClaudeConfig {
  apiKey: string;
  model: string;
  maxTokens: number;
  temperature: number;
}

export interface ClaudeMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ClaudeResponse {
  content: Array<{ text: string; type: string }>;
  id: string;
  model: string;
  role: string;
  stop_reason: string | null;
  stop_sequence: string | null;
  type: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

class ClaudeService {
  private config: ClaudeConfig;
  private baseUrl = (import.meta.env as any).PROD
    ? '/.netlify/functions/claude'  // Netlify Function in production
    : '/.netlify/functions/claude';  // Use Netlify Dev in development

  constructor() {
    // Initialize with defaults - API key will be loaded when needed
    this.config = {
      apiKey: '', // Will be loaded lazily
      model: 'claude-3-haiku-20240307',
      maxTokens: 1000,
      temperature: 0.7
    };
  }

  private getApiKey(): string {
    // Return cached key if available
    if (this.config.apiKey) {
      return this.config.apiKey;
    }

    // Only use localStorage for API key storage - never bundle keys in client
    const storedKey = localStorage.getItem('claude-api-key');
    if (storedKey) {
      this.config.apiKey = storedKey;
      return this.config.apiKey;
    }

    throw new Error('Claude API key not found. Configure API key in settings.');
  }

  private ensureApiKey(): boolean {
    try {
      this.getApiKey();
      return true;
    } catch (error) {
      return false;
    }
  }

  setApiKey(apiKey: string): void {
    this.config.apiKey = apiKey;
    localStorage.setItem('claude-api-key', apiKey);
  }

  async generateConversationalResponse(
    userMessage: string,
    context: ConversationContext
  ): Promise<ConversationResponse> {
    // Skip API key check - serverless function will handle it

    try {
      const systemPrompt = await this.buildSystemPrompt();
      const conversationHistory = this.buildConversationHistory(context);

      const response = await this.callClaude([
        { role: 'system', content: systemPrompt },
        ...conversationHistory,
        { role: 'user', content: userMessage }
      ]);

      return this.parseClaudeResponse(response, userMessage);
    } catch (error) {
      console.error('Claude API error:', error);
      throw new Error(`Failed to connect to Claude AI: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async buildSystemPrompt(): Promise<string> {
    // Get current lead data to provide context to Claude
    let leadDataContext = '';
    try {
      const unifiedDataService = (await import('../unifiedDataService')).default;

      // Get stable historical lead database for AI analysis
      const leads = await unifiedDataService.getLeads();

      console.log(`[Claude AI] Using historical lead database: ${leads.length} leads`);
      console.log(`[Claude AI] Stable lead names:`, leads.slice(0, 3).map(l => l.name));

      if (leads.length > 0) {
        leadDataContext = `

**CURRENT LEAD DATABASE (Historical database with stable leads) - ${leads.length} total leads:**
${leads.map((lead, index) =>
  `${index + 1}. ${lead.name} from ${lead.company} (${lead.email}) - Status: ${lead.status}, Value: $${lead.value.toLocaleString()}`
).join('\n')}

**DATA INTEGRITY GUIDELINES**:
• Use the ${leads.length} leads listed above as your data source
• When referencing specific leads, use the actual names and data from the list
• For general questions like "analyze", "prioritize", or "who should I go after first", provide helpful analysis using the available leads
• Be conversational and helpful - interpret user intent naturally
• If someone asks about a specific person not in the list, politely mention they're not in the current database and offer alternatives

**SIMPLE APPROACH:**
- Just have a normal conversation about business topics
- Use the lead data when relevant to answer questions
- Create content when asked (emails, proposals, etc.)
- Provide analysis and insights naturally
- Don't assume everything is a name search

**CONVERSATION STYLE:**
- Natural, friendly, and helpful
- Think like a business partner who knows the data
- Respond to any question or comment naturally
- Be curious and engaged in the conversation`;
      }
    } catch (error) {
      console.warn('Could not load lead data for system prompt:', error);
    }

    return `You are an AI sales assistant integrated into a GTM (Go-To-Market) dashboard. You're a strategic business advisor who can analyze real lead data and provide actionable insights.

**YOU ARE A SMART BUSINESS COLLEAGUE - NOT A SEARCH ENGINE**

You can discuss ANYTHING related to business, leads, sales, strategy, or general thoughts. You're having a normal conversation with a colleague who trusts your judgment.

**FUNDAMENTAL RULE: NEVER DEFAULT TO NAME SEARCHING**
- If someone says literally ANYTHING, interpret it as a normal human conversation first
- Only search for specific people when they clearly ask about a person by name
- Everything else is just normal business conversation

**YOU CAN HANDLE ANY TOPIC:**
- Random thoughts: "What do you think about..."
- Casual questions: "How's it going?"
- Business strategy: "Should we focus on..."
- Data requests: "Show me the numbers"
- Content creation: "Write something about..."
- Analysis: "What patterns do you see?"
- Opinions: "What's your take on this?"

**BE NATURAL AND HELPFUL - DON'T OVERTHINK IT**

**Your Core Capabilities:**
- **Data Analysis Expert**: Analyze the actual lead database to identify patterns, opportunities, and risks
- **Business Decision Advisor**: Provide strategic recommendations based on real data analysis
- **Lead Intelligence**: Answer any question about the leads in the database with detailed insights
- **Pipeline Strategy**: Optimize sales approach based on actual lead characteristics and behaviors

**Your Approach:**
- Think like a smart business partner who understands context and intent
- Interpret requests naturally - understand what humans actually mean
- Provide data-driven business recommendations with confidence
- Be conversational and intuitive, not literal or robotic
- Understand business language and respond intelligently

**What You Can Do:**
- Analyze lead distribution by company size, industry, location, deal value
- Identify high-value opportunities and explain why they're promising
- Recommend optimal sales strategies for different lead segments
- Compare leads and rank them by business potential
- Provide insights on market opportunities within the current pipeline
- **Draft emails, proposals, and outreach content** for specific leads using their data
- Create personalized sales materials based on lead characteristics
- Answer questions like "Who should I call first?", "What's our biggest opportunity?", "Which leads are ready to buy?"
- **Content Creation**: When asked to "draft", "write", or "create" something for a lead, generate that content using available lead data
- **Professional Email Style**: Use modern, clear, helpful tone (similar to Stripe/tech companies) - direct but friendly, value-focused, not overly salesy

**Business Decision Framework:**
When analyzing leads and making recommendations, consider:
- Deal value and revenue potential
- Lead engagement and readiness indicators
- Company characteristics (size, industry, growth stage)
- Geographic and market factors
- Timeline to close and sales cycle efficiency
- Risk factors and competitive positioning

**Response Style:**
- Be conversational and engaging - respond to ANY business question naturally
- Don't require specific formats or keywords - understand casual conversation
- Respond to open-ended questions like "How are things looking?" or "What do you think?"
- Engage with follow-up questions and clarifications naturally
- Be proactive and curious - ask questions back when helpful
- Treat every interaction as an ongoing business discussion

**EMAIL WRITING GUIDELINES:**
When drafting emails, follow these modern professional standards:

**TONE & STYLE (like Stripe, modern SaaS companies):**
- Clear and direct, but warm and human
- Professional without being stiff or corporate
- Helpful and value-focused, not pushy
- Modern language - avoid outdated business speak
- Conversational but respectful

**EMAIL STRUCTURE:**
- Clear, benefit-focused subject line
- Brief, friendly opening that shows you know their business
- Quickly establish value proposition relevant to their company/industry
- Include specific details from their lead profile (company, deal size, status)
- Clear but soft call-to-action
- Professional signature

**EXAMPLES OF GOOD VS. BAD TONE:**
✓ GOOD: "Hi Sarah, I noticed Strategic Industries has been growing rapidly..."
✗ BAD: "Dear Ms. Johnson, I hope this email finds you well..."

✓ GOOD: "We've helped similar companies in your industry reduce costs by 30%"
✗ BAD: "Our revolutionary solution will transform your business paradigms"

✓ GOOD: "Would you be interested in a brief 15-minute call to discuss?"
✗ BAD: "I would like to schedule a meeting at your earliest convenience"

**Data Access:**
You have complete access to the current lead database. Use this data to answer any question, provide analysis, and make business recommendations. Always reference specific leads by name when making recommendations.${leadDataContext}`;
  }

  private buildConversationHistory(context: ConversationContext): ClaudeMessage[] {
    return context.conversationHistory
      .slice(-10) // Keep last 10 messages for context
      .map(msg => ({
        role: msg.type === 'user' ? 'user' as const : 'assistant' as const,
        content: msg.content
      }))
      .filter(msg => msg.content.trim().length > 0);
  }

  private async callClaude(messages: ClaudeMessage[]): Promise<ClaudeResponse> {
    // No API key needed - serverless function handles authentication
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: this.config.model,
        max_tokens: this.config.maxTokens,
        temperature: this.config.temperature,
        messages: messages.filter(msg => msg.role !== 'system'),
        system: messages.find(msg => msg.role === 'system')?.content
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Claude API error: ${response.status} - ${errorData.error || 'Unknown error'}`);
    }

    return response.json();
  }

  private parseClaudeResponse(claudeResponse: ClaudeResponse, originalMessage: string): ConversationResponse {
    const content = claudeResponse.content[0]?.text || "I'm not sure how to respond to that.";

    // Analyze Claude's response to extract metadata
    const intent = this.extractIntent(originalMessage, content);
    const confidence = this.calculateConfidence(claudeResponse);
    const suggestedActions = this.extractSuggestedActions(content);
    const followUpQuestions = this.extractFollowUpQuestions(content);

    return {
      message: content,
      intent,
      confidence,
      suggestedActions: [], // Removed suggestions
      followUpQuestions: [] // Removed follow-up questions
    };
  }

  private extractIntent(userMessage: string, claudeResponse: string): string {
    const userLower = userMessage.toLowerCase();
    const responseLower = claudeResponse.toLowerCase();

    // Simple intent detection based on content
    if (userLower.includes('hello') || userLower.includes('hi') || userLower.includes('hey')) {
      return 'greeting';
    }
    if (userLower.includes('help') || responseLower.includes('here are the ways') || responseLower.includes('i can help')) {
      return 'help';
    }
    if (userLower.includes('qualify') || userLower.includes('lead') || responseLower.includes('qualification')) {
      return 'leadQualification';
    }
    if (userLower.includes('deal') || userLower.includes('opportunity') || responseLower.includes('opportunity')) {
      return 'opportunityAnalysis';
    }
    if (userLower.includes('email') || responseLower.includes('email')) {
      return 'emailComposition';
    }
    if (userLower.includes('pipeline') || responseLower.includes('pipeline')) {
      return 'pipelineOptimization';
    }
    if (userLower.includes('report') || responseLower.includes('report')) {
      return 'reporting';
    }
    if (userLower.includes('prioritize') || userLower.includes('ready to buy') || userLower.includes('best leads') ||
        userLower.includes('top leads') || responseLower.includes('priority leads')) {
      return 'leadPrioritization';
    }

    return 'general';
  }

  private calculateConfidence(claudeResponse: ClaudeResponse): number {
    // Higher confidence for longer, more detailed responses
    const responseLength = claudeResponse.content[0]?.text?.length || 0;
    const baseConfidence = Math.min(0.95, 0.7 + (responseLength / 1000) * 0.25);

    // Boost confidence if Claude provided specific actions or advice
    const content = claudeResponse.content[0]?.text?.toLowerCase() || '';
    if (content.includes('i recommend') || content.includes('you should') || content.includes('try this')) {
      return Math.min(0.95, baseConfidence + 0.1);
    }

    return baseConfidence;
  }

  private extractSuggestedActions(content: string): string[] {
    const actions: string[] = [];

    // Look for common action patterns
    const actionPatterns = [
      /try asking[^.!?]*/gi,
      /you (?:could|can|should)[^.!?]*/gi,
      /i recommend[^.!?]*/gi,
      /consider[^.!?]*/gi,
      /let me help you[^.!?]*/gi
    ];

    actionPatterns.forEach(pattern => {
      const matches = content.match(pattern);
      if (matches) {
        actions.push(...matches.slice(0, 3).map(match =>
          match.replace(/^(try asking|you (?:could|can|should)|i recommend|consider|let me help you)\s*/i, '').trim()
        ));
      }
    });

    // Add some default actions based on content
    if (content.toLowerCase().includes('lead')) {
      actions.push('Qualify a lead');
    }
    if (content.toLowerCase().includes('email')) {
      actions.push('Draft an email');
    }
    if (content.toLowerCase().includes('deal') || content.toLowerCase().includes('opportunity')) {
      actions.push('Analyze an opportunity');
    }

    return actions.slice(0, 4); // Limit to 4 suggestions
  }

  private extractFollowUpQuestions(content: string): string[] {
    const questions: string[] = [];

    // Look for questions in Claude's response
    const questionMatches = content.match(/[^.!?]*\?/g);
    if (questionMatches) {
      questions.push(...questionMatches
        .map(q => q.trim())
        .filter(q => q.length > 10 && q.length < 100)
        .slice(0, 3)
      );
    }

    // Add contextual follow-ups
    if (content.toLowerCase().includes('lead')) {
      questions.push('Do you have a specific lead you\'d like me to analyze?');
    }
    if (content.toLowerCase().includes('help')) {
      questions.push('What sales challenge are you working on today?');
    }

    return questions.slice(0, 3);
  }

  // Configuration methods
  updateConfig(newConfig: Partial<ClaudeConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  getConfig(): ClaudeConfig {
    return { ...this.config };
  }

  // Test the API connection
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.callClaude([
        { role: 'user', content: 'Hello' }
      ]);
      return response.content && response.content.length > 0;
    } catch (error) {
      console.error('Claude API test failed:', error);
      return false;
    }
  }
}

export const claudeService = new ClaudeService();